import type { UseScriptTagOptions } from '@vueuse/core'
import type {
  GsiButtonConfiguration,
  IdConfiguration,
  PromptMomentNotification,
} from 'google-one-tap'
import { watch } from 'vue'

interface Params {
  selector: string | Ref
  initializeConfig?: Partial<IdConfiguration>
  scriptTagOptions?: UseScriptTagOptions
  config?: GsiButtonConfiguration
  buttonClickHandler?: () => void
  promptCallback?: (notification: PromptMomentNotification) => void
  force?: boolean
}

interface RenderButtonParams {
  selector: string | Ref
  config?: GsiButtonConfiguration
  clickHandler?: Params['buttonClickHandler']
}

const getGoogleClientId = (initializeConfig: Partial<IdConfiguration>) => {
  return initializeConfig.client_id || useRuntimeConfig().public.googleClientId
}

const initialize = ({
  config,
  force,
}: {
  config: Partial<IdConfiguration>
  force: boolean
}) => {
  const isInitialized = useState('isInitialized', () => false)

  if (isInitialized.value && !force) return
  window.google.accounts.id.initialize({
    client_id: getGoogleClientId(config),
    use_fedcm_for_prompt: false,
    ...config,
  })
  isInitialized.value = true
}

const promptGoogleOneTap = (momentListener?: Params['promptCallback']) => {
  window.google.accounts.id.prompt(momentListener)
}

const renderButton = ({ selector, config, clickHandler }: RenderButtonParams) =>
  tryOnMounted(() => {
    const button =
      typeof selector === 'string'
        ? document.querySelector(selector)
        : selector.value

    if (!button) return
    window.google.accounts.id.renderButton(
      button,
      { locale: 'fr', ...config },
      clickHandler,
    )
  })

export const useGoogleAuth = ({
  initializeConfig = {},
  force = false,
  scriptTagOptions = {},
  deferScript = false,
}: {
  initializeConfig?: Partial<IdConfiguration>
  force?: boolean
  scriptTagOptions?: UseScriptTagOptions
  // load the Google script once the page is hydrated and the browser idle (One Tap of the home), instead of
  // competing with the page itself; a login form opened by the user loads it at once
  deferScript?: boolean
}) => {
  const { authenticatedTemp } = useAuthenticatedTemp()

  const scriptTagState = useState<HTMLScriptElement | null>(
    'scriptTag',
    () => null,
  )

  watch(
    authenticatedTemp,
    (isAuthenticated) => {
      if (isAuthenticated) {
        // signed in (One Tap, login form, finalized sign-up): close the One Tap
        // prompt, its iframe sits over the header buttons (notifications, profile)
        if (import.meta.client) window.google?.accounts?.id?.cancel()
        return
      }

      if (scriptTagState.value) {
        return initialize({ config: initializeConfig, force })
      }

      const loadScript = () => {
        const { scriptTag } = useScriptTag(
          'https://accounts.google.com/gsi/client',
          () => {
            initialize({ config: initializeConfig, force })
            syncRef(scriptTagState, scriptTag)
          },
          scriptTagOptions,
        )
      }
      if (deferScript) {
        onNuxtReady(loadScript)
      } else {
        loadScript()
      }
    },
    { immediate: true },
  )

  return {
    renderButton: (renderButtonParams: RenderButtonParams) =>
      whenever(scriptTagState, () => renderButton(renderButtonParams), {
        immediate: true,
      }),
    // the script stays loaded after signing in: coming back to a page that
    // prompts must not show One Tap to an account already signed in
    promptGoogleOneTap: (momentListener?: Params['promptCallback']) =>
      whenever(
        scriptTagState,
        () => {
          if (authenticatedTemp.value) return
          promptGoogleOneTap(momentListener)
        },
        { immediate: true },
      ),
  }
}
